import React, { useState } from 'react';
import { gameplayerAPI } from '../../services/api';

function toStr(v) { return (v?._id || v || '').toString(); }

const POSITION_OPTIONS = ['TW', 'LV', 'IV', 'RV', 'LM', 'ZM', 'DM', 'OM', 'RM', 'LA', 'RA', 'ST', 'HS'];

function PlayerRow({ entry, availablePlayers, onRemove, onUpdate }) {
    const name = entry.plid?.surname
        ? `${entry.plid.prename ? entry.plid.prename[0] + '. ' : ''}${entry.plid.surname}`
        : '–';
    const isStarter = entry.type !== 1;
    // num may be missing from gameplayer doc — look it up from availablePlayers
    const plid = toStr(entry.plid?._id || entry.plid);
    const pt = availablePlayers?.find(p => toStr(p.plid?._id || p.plid) === plid);
    const num = entry.num ?? pt?.num ?? '';

    return (
        <div className="flex items-center gap-2 py-1.5 border-b last:border-0">
            <div className="flex-none w-8 text-center font-mono text-sm text-slate-500">{num || ''}</div>
            <div className="flex-1 text-sm font-medium">{name}</div>
            <div className="flex-none">
                <input
                    type="text"
                    value={entry.positionDetail || entry.field || ''}
                    onChange={e => onUpdate(entry._id, { positionDetail: e.target.value, field: e.target.value })}
                    className="input input-xs input-bordered w-16 text-center"
                    placeholder="Pos"
                    list="positions-list"
                />
            </div>
            <div className="flex-none flex items-center gap-1">
                <button
                    className={`btn btn-xs ${entry.isCaptain ? 'btn-warning' : 'btn-ghost'}`}
                    onClick={() => onUpdate(entry._id, { isCaptain: !entry.isCaptain })}
                    title="Kapitän"
                >C</button>
                <button
                    className={`btn btn-xs ${isStarter ? 'btn-success' : 'btn-ghost'}`}
                    onClick={() => onUpdate(entry._id, { type: isStarter ? 1 : 0, isStarter: !isStarter })}
                    title={isStarter ? 'Starter' : 'Einwechslung'}
                >
                    {isStarter ? '▶' : '⬇'}
                </button>
                <button className="btn btn-xs btn-ghost text-error" onClick={() => onRemove(entry._id)}>✕</button>
            </div>
        </div>
    );
}

function playerLabel(pt) {
    const p = pt.plid;
    const num = pt.num ? `#${pt.num} ` : '';
    const name = p?.surname
        ? `${p.surname}${p.prename ? `, ${p.prename}` : ''}`
        : '?';
    return `${num}${name}`.trim();
}

function AddPlayerForm({ teamId, teamName, availablePlayers, existingIds, onAdd }) {
    const [plid, setPlid] = useState('');
    const [search, setSearch] = useState('');
    const [pos, setPos] = useState('');
    const [isStarter, setIsStarter] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    const eligible = availablePlayers
        .filter(pt => toStr(pt.tid) === toStr(teamId) && !existingIds.includes(toStr(pt.plid?._id || pt.plid)));

    const q = search.trim().toLowerCase();
    const filtered = eligible.filter(pt => {
        if (!q) return true;
        const p = pt.plid;
        const hay = `${pt.num || ''} ${p?.surname || ''} ${p?.prename || ''}`.toLowerCase();
        return hay.includes(q);
    });

    const pickPlayer = (pt) => {
        const id = toStr(pt.plid?._id || pt.plid);
        setPlid(id);
        setSearch(playerLabel(pt));
        setOpen(false);
    };

    const handleAdd = () => {
        if (!plid) { setError('Bitte Spieler wählen'); return; }
        setError('');
        onAdd({ plid, field: pos || 'N/A', positionDetail: pos, type: isStarter ? 0 : 1, isStarter, tid: teamId });
        setPlid('');
        setSearch('');
        setPos('');
    };

    return (
        <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-600 mb-2">Spieler hinzufügen – {teamName}</p>
            {error && <p className="text-error text-xs mb-2">{error}</p>}
            <div className="flex gap-2 flex-wrap items-start">
                <div className="relative flex-1 min-w-[180px]">
                    <input
                        type="text"
                        value={search}
                        placeholder="Spieler suchen…"
                        onChange={e => { setSearch(e.target.value); setPlid(''); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 150)}
                        className="input input-bordered input-sm w-full"
                    />
                    {open && filtered.length > 0 && (
                        <ul className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg text-sm">
                            {filtered.slice(0, 25).map(pt => {
                                const id = toStr(pt.plid?._id || pt.plid);
                                return (
                                    <li key={id}>
                                        <button
                                            type="button"
                                            className={`w-full text-left px-3 py-2 hover:bg-slate-100 ${plid === id ? 'bg-blue-50 font-medium' : ''}`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => pickPlayer(pt)}
                                        >
                                            {playerLabel(pt)}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {open && q && filtered.length === 0 && (
                        <div className="absolute z-20 mt-1 w-full px-3 py-2 bg-white border rounded-lg shadow text-xs text-slate-400">
                            Kein Spieler gefunden
                        </div>
                    )}
                </div>
                <input
                    type="text"
                    value={pos}
                    onChange={e => setPos(e.target.value)}
                    placeholder="Position"
                    className="input input-bordered input-sm w-20"
                    list="positions-list"
                />
                <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={isStarter} onChange={e => setIsStarter(e.target.checked)} className="checkbox checkbox-sm" />
                    Starter
                </label>
                <button className="btn btn-sm btn-primary" onClick={handleAdd}>+ Hinzufügen</button>
            </div>
        </div>
    );
}

export default function LineupTab({ game, gameId, lineup, availablePlayers, onLineupChange }) {
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState('');

    const homeId = toStr(game?.hid);
    const awayId = toStr(game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';

    const forTeam = (teamId) => lineup.filter(e => toStr(e.tid) === toStr(teamId));
    const existingIds = (teamId) => forTeam(teamId).map(e => toStr(e.plid?._id || e.plid));

    const handleAdd = async (teamId, data) => {
        setError('');
        setSaving('add');
        try {
            const pt = availablePlayers.find(p => toStr(p.plid?._id || p.plid) === toStr(data.plid));
            const res = await gameplayerAPI.create({
                gid: gameId,
                pid: game?.pid?._id || game?.pid,
                tid: teamId,
                plid: data.plid,
                num: pt?.num ?? null,
                field: data.field || 'N/A',
                positionDetail: data.positionDetail || '',
                type: data.type,
                isStarter: data.isStarter,
                minute: 0,
            });
            const enriched = { ...res.data, num: pt?.num, tid: teamId };
            onLineupChange(prev => [...prev, enriched]);
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Hinzufügen');
        } finally {
            setSaving(null);
        }
    };

    const handleUpdate = async (id, updates) => {
        setSaving(id);
        try {
            await gameplayerAPI.update(id, updates);
            onLineupChange(prev => prev.map(e => e._id === id ? { ...e, ...updates } : e));
        } catch (err) {
            setError('Fehler beim Aktualisieren');
        } finally {
            setSaving(null);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm('Spieler aus Aufstellung entfernen?')) return;
        setSaving(id);
        try {
            await gameplayerAPI.delete(id);
            onLineupChange(prev => prev.filter(e => e._id !== id));
        } catch (err) {
            setError('Fehler beim Entfernen');
        } finally {
            setSaving(null);
        }
    };

    const TeamSection = ({ teamId, teamName }) => {
        const players = forTeam(teamId);
        const starters = players.filter(p => p.type !== 1);
        const subs = players.filter(p => p.type === 1);

        return (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
                    <span className="font-bold">{teamName}</span>
                    <span className="text-xs text-slate-300">{starters.length} Starter · {subs.length} Bank</span>
                </div>
                <div className="px-4">
                    {starters.length > 0 && (
                        <>
                            <p className="text-xs text-slate-500 mt-3 mb-1 font-semibold uppercase tracking-wide">Startelf</p>
                            {starters.map(e => (
                                <PlayerRow key={e._id} entry={e} availablePlayers={availablePlayers} onRemove={handleRemove} onUpdate={handleUpdate} />
                            ))}
                        </>
                    )}
                    {subs.length > 0 && (
                        <>
                            <p className="text-xs text-slate-500 mt-3 mb-1 font-semibold uppercase tracking-wide">Ersatzbank</p>
                            {subs.map(e => (
                                <PlayerRow key={e._id} entry={e} availablePlayers={availablePlayers} onRemove={handleRemove} onUpdate={handleUpdate} />
                            ))}
                        </>
                    )}
                    <AddPlayerForm
                        teamId={teamId}
                        teamName={teamName}
                        availablePlayers={availablePlayers}
                        existingIds={existingIds(teamId)}
                        onAdd={(data) => handleAdd(teamId, data)}
                    />
                    <div className="py-2" />
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-4">
            {error && (
                <div className="alert alert-error text-sm">
                    <span>⚠️ {error}</span>
                    <button className="btn btn-xs btn-ghost ml-auto" onClick={() => setError('')}>✕</button>
                </div>
            )}
            {saving === 'add' && <div className="text-center text-slate-400 text-sm">Speichern...</div>}
            <TeamSection teamId={homeId} teamName={homeName} />
            <TeamSection teamId={awayId} teamName={awayName} />
            <datalist id="positions-list">
                {POSITION_OPTIONS.map(p => <option key={p} value={p} />)}
            </datalist>
        </div>
    );
}
